import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { tasksService } from '../services/tasksService';
import taskPaymentsService from '../services/taskPaymentsService';
import { clientsService } from '../services/clientsService';
import { objectsService } from '../services/objectsService';
import PullToRefresh from '../components/PullToRefresh';
import syncService from '../services/syncService';
import { toast } from '../utils/toast';
import { normalizeDueDateForSubmit } from '../utils/normalizeDueDateForSubmit';
import { taskServerId } from '../utils/taskServerId';
import TaskForm from '../components/TaskForm';
import TaskWaitingDialog from '../components/TaskWaitingDialog';
import WeekCalendarView from '../components/WeekCalendarView';
import { TASK_COLORS } from '../components/TaskForm';
import { isPersonalTask } from '../utils/taskKind';
import {
  DEFAULT_TASK_SCHEDULE,
  buildTaskDueDate,
  taskDueDateForForm,
  taskScheduleFormFields,
} from '../utils/taskSchedule';
import './Dashboard.css';

const toDateKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [objects, setObjects] = useState([]);
  const [weekStartKey, setWeekStartKey] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d);
    monday.setDate(diff);
    return toDateKey(monday);
  });
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [waitingTask, setWaitingTask] = useState(null);
  const [waitingSaving, setWaitingSaving] = useState(false);
  const [paymentCreatingTaskId, setPaymentCreatingTaskId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    object_id: '',
    due_date: '',
    ...DEFAULT_TASK_SCHEDULE,
    priority: 'medium',
    cost: 0,
    category: '',
    color: TASK_COLORS[1].value,
    address: '',
    carry_items: [],
    status: 'in_progress',
    is_recurring: false,
    recurrence_type: '',
    expenses: [],
    is_personal: false,
  });

  const loadTasks = useCallback(async () => {
    try {
      const response = await tasksService.getAll();
      let list = [];
      if (response) {
        if (Array.isArray(response)) list = response;
        else if (response.data) list = Array.isArray(response.data) ? response.data : [];
      }
      // На дашборде не показываем завершённые задачи без названия (пустые блоки)
      const validList = (list || []).filter(
        (t) => t && (t.id != null || t.server_id != null) && (t.status !== 'completed' || (t.title && String(t.title).trim() !== ''))
      );
      setTasks(validList);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const response = await clientsService.getAll();
      setClients(response.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  }, []);

  const loadObjects = useCallback(async () => {
    try { setObjects(await objectsService.getAll({ status: 'active' })); }
    catch (error) { console.error('Error loading objects:', error); }
  }, []);

  useEffect(() => {
    void loadTasks();
    void loadClients();
    void loadObjects();
    const handleFocus = () => void loadTasks();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('crm:sync-completed', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('crm:sync-completed', handleFocus);
    };
  }, [loadTasks, loadClients, loadObjects]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      client_id: '',
      object_id: '',
      due_date: '',
      ...DEFAULT_TASK_SCHEDULE,
      priority: 'medium',
      cost: 0,
      category: '',
      color: TASK_COLORS[1].value,
      address: '',
      carry_items: [],
      status: 'in_progress',
      is_recurring: false,
      recurrence_type: '',
      expenses: [],
      is_personal: false,
    });
  };

  const handleSubmit = async (submitData) => {
    try {
      const payload = { ...submitData };
      let savedTask = null;
      normalizeDueDateForSubmit(payload);
      if (editingTask) {
        if (!payload.status) payload.status = editingTask.status || 'in_progress';
        if (payload.status === 'pending' || payload.status === 'cancelled') payload.status = 'in_progress';
        savedTask = await tasksService.update(taskServerId(editingTask), payload);
      } else {
        payload.status = 'in_progress';
        savedTask = await tasksService.create(payload);
      }
      const queued = savedTask?.sync_status === 'pending'
        || savedTask?.queuedForSync === true
        || savedTask?.offline === true;
      toast.success(queued
        ? 'Задача сохранена на телефоне и синхронизируется при появлении сети'
        : (editingTask ? 'Задача обновлена' : 'Задача создана'));
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      if (queued) {
        const local = await tasksService.getLocalTasks();
        setTasks(local || []);
      } else {
        await loadTasks();
      }
      return true;
    } catch (error) {
      console.error('Error saving task:', error);
      if (error.queuedForSync) {
        toast.success('Сервер недоступен. Задача сохранена и будет отправлена при восстановлении связи.');
        setShowModal(false);
        setEditingTask(null);
        resetForm();
        await tasksService.getLocalTasks().then((local) => setTasks(local || []));
        return true;
      } else {
        const msg = error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504
          ? 'Сервер временно недоступен. Попробуйте позже.'
          : (error.response?.data?.error || 'Ошибка при сохранении задачи');
        toast.error(msg);
        return false;
      }
    }
  };

  const handleEdit = async (task) => {
    try {
      const id = taskServerId(task);
      const full = await tasksService.getById(id);
      const t = (full && full.data) ? full.data : (full || task);

      let payment = null;

      try {
        const paymentResult =
          await taskPaymentsService.getForTask(id);

        payment = paymentResult.payment || null;
      } catch (paymentError) {
        console.error(
          'Error loading task payment:',
          paymentError
        );
      }

      setEditingTask({
        ...t,
        payment,
      });

      const dueDate = taskDueDateForForm(t);
      let taskStatus = t.status || 'in_progress';
      if (taskStatus === 'pending' || taskStatus === 'cancelled') taskStatus = 'in_progress';
      setFormData({
        title: t.title,
        description: t.description || '',
        client_id: t.client_id || '',
        object_id: t.object_id || '',
        due_date: dueDate,
        ...taskScheduleFormFields(t),
        priority: t.priority || 'medium',
        cost: t.cost || 0,
        category: t.category || '',
        color: t.color || TASK_COLORS[1].value,
        is_recurring: t.is_recurring || false,
        recurrence_type: t.recurrence_type || '',
        address: t.address || '',
        carry_items: Array.isArray(t.carry_items) ? t.carry_items : [],
        status: taskStatus,
        expenses: Array.isArray(t.expenses) ? t.expenses.map(e => ({ amount: e.amount, category: e.category, description: e.description || '' })) : [],
        is_personal: isPersonalTask(t),
      });
      setShowModal(true);
    } catch (err) {
      console.error('Error loading task details', err);
    }
  };

  const handleDeleteFromForm = async (id) => {
    try {
      await tasksService.delete(id);
      toast.success('Задача удалена');
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      void loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.error || 'Ошибка при удалении задачи');
    }
  };

  const handleComplete = async (id) => {
    try {
      const response = await tasksService.complete(id);
      const resData = (response && response.data) ? response.data : (response || {});
      const message = resData.message || 'Задача выполнена!';
      const amountDue = Number(resData.amount_due || 0);
      toast.success(`${message}${amountDue > 0 ? ` К оплате: ${amountDue} ₽` : ''}`, { autoClose: 3000 });
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      void loadTasks();
    } catch (error) {
      if (error.queuedForSync) {
        toast.success('Задача отмечена выполненной и будет синхронизирована с сервером');
        setShowModal(false);
        setEditingTask(null);
        resetForm();
        void tasksService.getLocalTasks().then((local) => setTasks(local || []));
      } else {
        console.error('Error completing task:', error);
        toast.error(error.response?.data?.error || 'Ошибка при завершении задачи');
      }
    }
  };

  const handleReopen = async (id) => {
    try {
      const response = await tasksService.reopen(id);
      toast.success(response?.data?.message || 'Задача возвращена в работу');
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      await loadTasks();
    } catch (error) {
      console.error('Error reopening task:', error);
      toast.error(error.response?.data?.error || 'Ошибка при возврате задачи в работу');
    }
  };

  const handleStartWaiting = (task = editingTask) => {
    if (task) setWaitingTask(task);
  };

  const handleSaveWaiting = async (payload) => {
    if (!waitingTask) return;
    setWaitingSaving(true);
    try {
      const result = await tasksService.wait(taskServerId(waitingTask), payload);
      const updated = result.task || { ...waitingTask, ...payload, status: 'waiting' };
      setTasks(previous => previous.map(task => (
        taskServerId(task) === taskServerId(waitingTask)
          ? { ...task, ...updated }
          : task
      )));
      setEditingTask(previous => (
        previous && taskServerId(previous) === taskServerId(waitingTask)
          ? {
              ...previous,
              ...updated,
              history: result.history_event
                ? [result.history_event, ...(previous.history || [])]
                : previous.history,
            }
          : previous
      ));
      setFormData(previous => ({ ...previous, status: 'waiting' }));
      setWaitingTask(null);
      toast.success(result.message || 'Задача переведена в ожидание');
      void loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось отложить задачу');
    } finally {
      setWaitingSaving(false);
    }
  };

  const handleResumeWaiting = async () => {
    if (!editingTask) return;
    try {
      const result = await tasksService.resume(taskServerId(editingTask));
      const updated = result.task || { ...editingTask, status: 'in_progress' };
      setEditingTask(previous => ({
        ...previous,
        ...updated,
        history: result.history_event
          ? [result.history_event, ...(previous.history || [])]
          : previous.history,
      }));
      setFormData(previous => ({ ...previous, status: 'in_progress' }));
      setTasks(previous => previous.map(task => (
        taskServerId(task) === taskServerId(editingTask)
          ? { ...task, ...updated }
          : task
      )));
      toast.success(result.message || 'Задача возвращена в работу');
      void loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось вернуть задачу в работу');
    }
  };

  const handleAddHistory = async (comment) => {
    if (!editingTask) return null;
    try {
      const event = await tasksService.addHistory(taskServerId(editingTask), comment);
      setEditingTask(previous => ({
        ...previous,
        history: [event, ...(previous.history || [])],
      }));
      toast.success('Заметка добавлена');
      return event;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось добавить заметку');
      throw error;
    }
  };

  const handleMarkPaid = async (id, paymentDate = null) => {
    if (!id) return;
    try {
      const response = await tasksService.markPaid(id, paymentDate);
      const resData = (response && response.data) ? response.data : (response || {});
      const income = resData.income || 0;
      toast.success(`Задача отмечена как оплаченная${income > 0 ? ` (+${income} ₽)` : ''}`);
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      void loadTasks();
    } catch (error) {
      console.error('Error marking task as paid:', error);
      toast.error(error.response?.data?.error || 'Ошибка при отметке задачи как оплаченной');
    }
  };

  const editingTaskId = editingTask
    ? taskServerId(editingTask)
    : null;
  const editingPaymentId = editingTask?.payment?.id || null;
  const editingPaymentStatus =
    editingTask?.payment?.status ?? null;
  const editingPaymentFinanceId =
    editingTask?.payment?.finance_id || null;
  const editingPaymentProcessed =
    Number(editingPaymentStatus) === 1
    && Boolean(editingPaymentFinanceId);

  useEffect(() => {
    if (
      !showModal
      || !editingPaymentId
      || editingPaymentProcessed
      || [3, 4].includes(Number(editingPaymentStatus))
    ) {
      return undefined;
    }

    let cancelled = false;
    let checking = false;

    const checkPayment = async () => {
      if (checking || cancelled) return;

      checking = true;

      try {
        const result = await taskPaymentsService.check(
          editingPaymentId
        );
        const updatedPayment = result.payment;

        if (!updatedPayment || cancelled) return;

        if (Number(updatedPayment.status) === 1) {
          const completedAt = new Date().toISOString();

          setEditingTask((previous) => (
            previous
            && previous.payment?.id === updatedPayment.id
              ? {
                  ...previous,
                  status: 'completed',
                  completed_at:
                    previous.completed_at || completedAt,
                  is_paid: true,
                  payment_status: 'paid',
                  payment: updatedPayment,
                }
              : previous
          ));
          setFormData((previous) => ({
            ...previous,
            status: 'completed',
          }));
          setTasks((previous) => previous.map((item) => (
            taskServerId(item) === editingTaskId
              ? {
                  ...item,
                  status: 'completed',
                  completed_at:
                    item.completed_at || completedAt,
                  is_paid: true,
                  payment_status: 'paid',
                }
              : item
          )));

          toast.success(
            `Оплата получена: ${Number(updatedPayment.amount || 0)
              .toLocaleString('ru-RU')} ₽`
          );
          void loadTasks();
        } else {
          setEditingTask((previous) => (
            previous
            && previous.payment?.id === updatedPayment.id
              ? {
                  ...previous,
                  payment: updatedPayment,
                }
              : previous
          ));
        }
      } catch (error) {
        console.error(
          'Payment status check error:',
          error
        );
      } finally {
        checking = false;
      }
    };

    void checkPayment();
    const intervalId = window.setInterval(
      checkPayment,
      5000
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    showModal,
    editingTaskId,
    editingPaymentId,
    editingPaymentStatus,
    editingPaymentFinanceId,
    editingPaymentProcessed,
    loadTasks,
  ]);

  const handleCreatePayment = async (
    task = editingTask,
    paymentDetails = {}
  ) => {
    if (!task) return;

    const id = taskServerId(task);
    const workAmount = Number(
      paymentDetails.workAmount || 0
    );
    const equipmentAmount = Number(
      paymentDetails.equipmentAmount || 0
    );
    const amount = workAmount + equipmentAmount;

    if (!id) {
      toast.error(
        'Не удалось определить ID задачи'
      );
      return false;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error(
        'Укажи стоимость работы или оборудования'
      );
      return false;
    }

    const normalizedTitle =
      String(
        paymentDetails.title || task.title || ''
      ).trim();

    if (!normalizedTitle) {
      toast.error(
        'Название услуги не может быть пустым'
      );
      return false;
    }

    setPaymentCreatingTaskId(id);

    try {
      const result =
        await taskPaymentsService.createForTask(
          id,
          normalizedTitle,
          {
            workAmount,
            equipmentAmount,
          }
        );

      const payment = result.payment;

      if (!payment) {
        throw new Error(
          'CRM не вернула данные платежа'
        );
      }

      setEditingTask((previous) => (
        previous
        && taskServerId(previous) === id
          ? {
              ...previous,
              payment,
            }
          : previous
      ));

      const paymentLink =
        payment.sbp_url
        || payment.payment_url;

      if (
        paymentLink
        && navigator.clipboard?.writeText
      ) {
        try {
          await navigator.clipboard.writeText(
            paymentLink
          );
        } catch (clipboardError) {
          console.warn(
            'Не удалось скопировать ссылку:',
            clipboardError
          );
        }
      }

      toast.success(
        result.reused
          ? 'Активный платёж уже существовал. Ссылка скопирована.'
          : 'Платёж создан. Ссылка скопирована.'
      );
      return true;
    } catch (error) {
      console.error(
        'Create task payment error:',
        error
      );

      toast.error(
        error.response?.data?.error
        || error.message
        || 'Не удалось создать платёж'
      );
      return false;
    } finally {
      setPaymentCreatingTaskId(null);
    }
  };

  const openAddTask = (dueDate = null) => {
    setEditingTask(null);
    let initialDue = '';
    if (dueDate) {
      const d = new Date(dueDate);
      initialDue = buildTaskDueDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, false);
    }
    setFormData(prev => ({ ...prev, ...DEFAULT_TASK_SCHEDULE, due_date: initialDue }));
    setShowModal(true);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await syncService.syncAll();
      await loadTasks();
      await loadClients();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} disabled={loading || refreshing}>
      <div className="dashboard dashboard-tasks-only">
        <WeekCalendarView
          tasks={tasks}
          weekStartKey={weekStartKey}
          setWeekStartKey={setWeekStartKey}
          onTaskClick={handleEdit}
          onAddTask={openAddTask}
        />
      </div>

      {showModal && (
        <TaskForm
          formData={formData}
          setFormData={setFormData}
          clients={clients}
          objects={objects}
          onSubmit={handleSubmit}
          onCancel={() => { setShowModal(false); setEditingTask(null); resetForm(); }}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onWait={handleStartWaiting}
          onResumeWaiting={handleResumeWaiting}
          onAddHistory={handleAddHistory}
          onDelete={handleDeleteFromForm}
          onMarkPaid={handleMarkPaid}
          onCreatePayment={handleCreatePayment}
          paymentCreatingTaskId={paymentCreatingTaskId}
          editingTask={editingTask}
        />
      )}
      <TaskWaitingDialog
        task={waitingTask}
        saving={waitingSaving}
        onClose={() => !waitingSaving && setWaitingTask(null)}
        onSave={handleSaveWaiting}
      />
    </PullToRefresh>
  );
};

export default Dashboard;
